import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiUpload, FiX, FiCheck, FiUsers, FiBarChart2, FiSettings, FiArrowRight } from 'react-icons/fi';
import { connect } from 'react-redux';
import { getCookie } from '../utils/cookies';
import toast from 'react-hot-toast';
import Confetti from 'react-confetti';
import { useNavigate } from 'react-router-dom';

const generateUniqueFileName = (originalFileName) => {
  const timestamp = new Date().getTime();
  const randomString = Math.random().toString(36).substring(2, 8);
  const extension = originalFileName.split('.').pop();
  return `${timestamp}-${randomString}.${extension}`;
};

const CelebrationPopup = ({ onClose }) => {
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
    >
      <Confetti
        width={windowSize.width}
        height={windowSize.height}
        recycle={false}
        numberOfPieces={200}
      />
      <motion.div
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.5, opacity: 0 }}
        className="relative w-full max-w-md p-8 mx-4 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl border border-gray-700/50 text-center"
      >
        <div className="mb-6 flex justify-center">
          <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-green-500 rounded-full flex items-center justify-center">
            <FiCheck className="w-8 h-8 text-white" />
          </div>
        </div>
        <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 text-transparent bg-clip-text mb-4">
          Thank you!
        </h2>
        <p className="text-gray-300 text-lg mb-8">
          Your schedule has been successfully uploaded and processed.
        </p>
        <button
          onClick={onClose}
          className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-blue-500/20"
        >
          Continue
        </button>
      </motion.div>
    </motion.div>
  );
};

const AdminWelcomePopup = ({ isOpen, onClose, userName }) => {
  const navigate = useNavigate();

  const handleAccessDashboard = () => {
    onClose();
    navigate('/admin-dashboard');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="relative w-full max-w-2xl p-8 mx-4 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl border border-gray-700/50"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
            >
              <FiX className="w-6 h-6" />
            </button>

            <div className="text-center space-y-6 mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto">
                <FiSettings className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 text-transparent bg-clip-text">
                Welcome, Administrator!
              </h2>
              <p className="text-gray-300 text-lg max-w-md mx-auto">
                Welcome back, {userName}. Access your admin dashboard to manage users, view analytics, and oversee system operations.
              </p>
            </div>

            {/* Quick Stats Preview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-blue-600/20 border border-blue-500/30 rounded-lg p-4 text-center">
                <FiUsers className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                <p className="text-blue-400 font-semibold">User Management</p>
                <p className="text-gray-400 text-sm">Manage student accounts</p>
              </div>
              <div className="bg-purple-600/20 border border-purple-500/30 rounded-lg p-4 text-center">
                <FiBarChart2 className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                <p className="text-purple-400 font-semibold">Analytics</p>
                <p className="text-gray-400 text-sm">View system metrics</p>
              </div>
              <div className="bg-green-600/20 border border-green-500/30 rounded-lg p-4 text-center">
                <FiSettings className="w-8 h-8 text-green-400 mx-auto mb-2" />
                <p className="text-green-400 font-semibold">System Control</p>
                <p className="text-gray-400 text-sm">Configure settings</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-end">
              <button
                onClick={onClose}
                className="px-6 py-3 text-gray-400 hover:text-white transition-colors"
              >
                Maybe Later
              </button>
              <button
                onClick={handleAccessDashboard}
                className="flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-xl font-medium transition-all duration-200 shadow-lg hover:shadow-purple-500/20"
              >
                <span>Access Admin Dashboard</span>
                <FiArrowRight className="w-5 h-5" />
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const WelcomeUploadPopup = ({ isOpen, onClose, email, isAdmin, userName, membership }) => {
  // All hooks must be called at the top level, before any conditional logic
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(''); // '', 'getting-url', 'uploading', 'processing', 'done', 'error'
  const [showCelebration, setShowCelebration] = useState(false);
  const [showMainPopup, setShowMainPopup] = useState(true);

  // Reset showMainPopup when the component is opened
  useEffect(() => {
    if (isOpen) {
      setShowMainPopup(true);
    }
  }, [isOpen]);

  // If user is admin, show admin welcome popup instead
  if (membership === "Admin") {
    return <AdminWelcomePopup isOpen={isOpen} onClose={onClose} userName={userName} />;
  }

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      setUploadStatus('');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      setUploadStatus('');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || uploadStatus === 'getting-url' || uploadStatus === 'uploading' || uploadStatus === 'processing') {
      return;
    }

    const waitingToast = toast.loading(
      <div className="flex flex-col">
        <span className="text-lg font-bold">Uploading Schedule</span>
        <span className="text-sm">Please wait while we process your schedule...</span>
      </div>,
      {
        position: 'top-center',
        style: {
          background: '#3B82F6',
          color: 'white',
          padding: '16px',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        },
      }
    );

    try {
      setUploadStatus('getting-url');

      // Generate unique filename
      const uniqueFileName = generateUniqueFileName(selectedFile.name);
      const s3Key = `uploads/${uniqueFileName}`;
      const contentType = selectedFile.type || 'image/png'; // Default to image/png if type is not available

      const presignResponse = await fetch('https://api.ruturbo.com/process-schedule', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: getCookie('session_id'),
        },
        body: JSON.stringify({
          email: email,
          action: 'generateUrl',
          key: s3Key,
          contentType: contentType // Add the contentType parameter
        }),
      });

      if (!presignResponse.ok) {
        throw new Error(`Failed to get URL: ${presignResponse.status}`);
      }

      const responseData = await presignResponse.json();
      const presignedUrl = responseData.url;
      
      if (!presignedUrl) {
        throw new Error('No URL returned from server');
      }

      // Upload to S3
      setUploadStatus('uploading');
      const uploadResult = await fetch(presignedUrl, {
        method: 'PUT',
        body: selectedFile
      });

      if (!uploadResult.ok) {
        throw new Error(`S3 upload failed: ${uploadResult.status}`);
      }

      // Process the uploaded image
      setUploadStatus('processing');
      const processResponse = await fetch('https://api.ruturbo.com/process-schedule', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: getCookie('session_id'),
        },
        body: JSON.stringify({
          email: email,
          action: 'extractText',
          key: s3Key,
        }),
      });

      if (!processResponse.ok) {
        throw new Error(`Processing failed: ${processResponse.status}`);
      }

      setUploadStatus('done');
      toast.dismiss(waitingToast);
      
      // First hide the main popup
      setShowMainPopup(false);
      
      // Show celebration popup after a short delay
      setTimeout(() => {
        setShowCelebration(true);
      }, 300); // Delay matches the exit animation duration
    } catch (err) {
      console.error('Error:', err);
      setUploadStatus('error');
      toast.dismiss(waitingToast);
      toast.error(
        <div className="flex flex-col">
          <span className="text-lg font-bold">Error</span>
          <span className="text-sm">{err.message || 'Failed to process schedule. Please try again.'}</span>
        </div>,
        {
          duration: 4000,
          position: 'top-center',
          style: {
            background: '#EF4444',
            color: 'white',
            padding: '16px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          },
        }
      );
    }
  };

  const handleCelebrationClose = () => {
    setShowCelebration(false);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <AnimatePresence>
            {showMainPopup && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
              >
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  className="relative w-full max-w-2xl p-8 mx-4 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl border border-gray-700/50"
                >
                  <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
                  >
                    <FiX className="w-6 h-6" />
                  </button>

                  <div className="text-center space-y-4 mb-8">
                    <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 text-transparent bg-clip-text">
                      Welcome back!
                    </h2>
                    <p className="text-gray-300 text-lg">
                      Please upload a screenshot of your current WebReg schedule. This will help our administrators add new sections at times that are most convenient for you, ensuring you get the classes you need.
                    </p>
                  </div>

                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                      isDragging
                        ? 'border-blue-400 bg-blue-400/10'
                        : 'border-gray-600 hover:border-gray-500 bg-gray-800/50'
                    }`}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileSelect}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      disabled={uploadStatus === 'getting-url' || uploadStatus === 'uploading' || uploadStatus === 'processing'}
                    />
                    <div className="space-y-4">
                      <div className="flex justify-center">
                        <FiUpload className={`w-12 h-12 ${isDragging ? 'text-blue-400' : 'text-gray-400'}`} />
                      </div>
                      <div>
                        <p className="text-lg font-medium text-gray-300">
                          {selectedFile
                            ? selectedFile.name
                            : 'Drag and drop your screenshot here'}
                        </p>
                        <p className="text-sm text-gray-400 mt-2">
                          {selectedFile
                            ? `${(selectedFile.size / 1024 / 1024).toFixed(2)} MB`
                            : 'or click to browse'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex justify-end">
                    <button
                      onClick={handleUpload}
                      disabled={!selectedFile || uploadStatus === 'getting-url' || uploadStatus === 'uploading' || uploadStatus === 'processing'}
                      className={`px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
                        selectedFile && uploadStatus !== 'getting-url' && uploadStatus !== 'uploading' && uploadStatus !== 'processing'
                          ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg hover:shadow-blue-500/20'
                          : 'bg-gray-700 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {uploadStatus === 'getting-url'
                        ? 'Getting URL...'
                        : uploadStatus === 'uploading'
                        ? 'Uploading...'
                        : uploadStatus === 'processing'
                        ? 'Processing...'
                        : 'Upload Schedule'}
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {showCelebration && (
              <CelebrationPopup onClose={handleCelebrationClose} />
            )}
          </AnimatePresence>
        </>
      )}
    </AnimatePresence>
  );
};

const mapStateToProps = (state) => ({
  email: state.profile.email,
  isAdmin: state.profile.isAdmin,
  userName: state.profile.userName,
  membership: state.profile.membership
});

export default connect(mapStateToProps)(WelcomeUploadPopup);
